import React, { createContext, useMemo } from 'react';
import PropTypes from 'prop-types';

import { checkChildren, filterChildrenOnType } from '@hooks';
import { mergeClasses } from '@utils';

import InputErrorMessage from './Input.ErrorMessage';
import InputHelperMessage from './Input.HelperMessage';
import InputLabel from './Input.Label';

export const InputContext = createContext();

/**
 * Input component that serves as a container for its child components:
 * `Input.ErrorMessage`, `Input.HelperMessage`, and `Input.Label`.
 * Requires at least one child of type `Input.ErrorMessage` and `Input.Label`.
 *
 * @component Input
 * @param {Object} props - The properties for the Input component.
 * @param {React.ReactNode} props.children - The child components of the Input. Must include at least one `Input.ErrorMessage` and `Input.Label` child component.
 * @param {string} [props.className] - Optional additional CSS classes to apply to the Input container.
 * @param {boolean} [props.isValid] - Optional flag to indicate if the input is valid.
 * @param {Object} [restProps] - Additional properties to be passed to the Input container (such as placeholder, type, etc.).
 * @returns {ReactElement} The rendered Input component.
 * @example
 * <Input placeholder="John Doe" type="text">
 *   <Input.Label>Name</Input.Label>
 *   <Input.ErrorMessage>
 *     Please enter your full name, including your first name and last name.
 *   </Input.ErrorMessage>
 *   <Input.HelperMessage>
 *     Please enter your full name, including your first name and last name.
 *   </Input.HelperMessage>
 * </Input>
 */

export default function Input({ children, className, isValid, ...restProps }) {
  checkChildren(children, [
    Input.ErrorMessage,
    Input.HelperMessage,
    Input.Label
  ]);

  if (['radio', 'checkbox'].includes(restProps.type)) {
    throw new Error(
      "Invalid 'type' property in 'Input'. 'radio' and 'checkbox' types are not supported. Use 'Radio' or 'Checkbox' components instead."
    );
  }

  let hasErrorMessage = false;
  let hasLabel = false;

  React.Children.forEach(children, child => {
    if (child.type === Input.ErrorMessage) {
      hasErrorMessage = true;
    }
    if (child.type === Input.Label) {
      hasLabel = true;
    }
  });

  if (!hasErrorMessage) {
    throw new Error(
      "Missing required components in 'Input'. 'Input.ErrorMessage' component is required."
    );
  }

  if (!hasLabel) {
    throw new Error(
      "Missing required components in 'Input'. 'Input.Label' component is required."
    );
  }

  const [labelChild, nonLabelChildren] = filterChildrenOnType(
    [Input.Label],
    children
  );

  const contextValue = useMemo(
    () => ({
      isValid,
      isRequired: restProps.required || false
    }),
    [isValid]
  );

  return (
    <InputContext.Provider value={contextValue}>
      <label className="space-y-2">
        {labelChild}
        <div className="relative">
          <input
            className={mergeClasses(
              'peer/input form-input m-0 box-border w-full rounded-md border-solid bg-white p-3 font-sans text-base font-bold text-black placeholder:font-normal placeholder:text-gray focus:border-black focus:ring-1 focus:ring-black',
              {
                'border-cinnebar': isValid === false
              },
              className
            )}
            {...restProps}
          />
          {isValid === false && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              className="absolute right-3 top-1/2 box-border h-6 w-6 -translate-y-1/2 rounded-full bg-cinnebar fill-white p-1 peer-focus/input:invisible"
            >
              <path d="M12.0007 10.5865L16.9504 5.63672L18.3646 7.05093L13.4149 12.0007L18.3646 16.9504L16.9504 18.3646L12.0007 13.4149L7.05093 18.3646L5.63672 16.9504L10.5865 12.0007L5.63672 7.05093L7.05093 5.63672L12.0007 10.5865Z" />
            </svg>
          )}
          {isValid === true && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              className="absolute right-3 top-1/2 box-border h-6 w-6 -translate-y-1/2 rounded-full bg-green fill-white p-1 peer-focus/input:invisible"
            >
              <path d="M10.0007 15.1709L19.1931 5.97852L20.6073 7.39273L10.0007 17.9993L3.63672 11.6354L5.05093 10.2212L10.0007 15.1709Z" />
            </svg>
          )}
        </div>
        {nonLabelChildren}
      </label>
    </InputContext.Provider>
  );
}

Input.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  isValid: PropTypes.bool
};

Input.defaultProps = {
  className: '',
  isValid: undefined
};

Input.ErrorMessage = InputErrorMessage;
Input.HelperMessage = InputHelperMessage;
Input.Label = InputLabel;
